import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronRight,
  ChevronLeft,
  Loader2,
  Check,
  ShieldCheck,
  Lock,
  PartyPopper,
  Home,
  Clock,
  ListChecks,
  Camera,
  FileText,
  AlertCircle,
  CheckCircle2,
  ChevronLeft as Chevron,
} from 'lucide-react';
import { useAuth } from '@/store/auth';
import { ChoicePicker } from '@/components/ChoicePicker';
import { NextStep } from '@/components/NextStep';
import { PortalTourCard } from '@/tour/PortalTourCard';
import { OfficeCard } from '@/components/OfficeCard';
import { BrandBand } from '@/components/BrandBand';
import { WelcomeHome, shouldWelcome } from '@/components/WelcomeHome';
import { WhatsIncluded } from '@/components/WhatsIncluded';
import { useBrand } from '@/lib/brand';
import { ConfirmIdentity } from '@/components/ConfirmIdentity';
import { REAUTH_CODE } from '@/lib/google';
import {
  api,
  ApiError,
  putToStorage,
  type FieldOptions,
  type OptionField,
  type Profile,
  type ProfilePatch,
  type ResidentDoc,
  type ResidentDocType,
} from '@/lib/api';
import { isValidType, isValidSize, prepareForUpload } from '@/lib/imageCompression';

/** First name for the greeting; records loaded with only a full name use its first word. */
function greetName(p: { firstName?: string | null; fullName?: string | null }): string {
  return (p.firstName || p.fullName?.trim().split(/\s+/)[0] || '').trim();
}

type FieldKey = keyof ProfilePatch;
interface Field {
  key: FieldKey | 'idMethod';
  label: string;
  type?: 'text' | 'tel' | 'date' | 'number' | 'select' | 'pills';
  placeholder?: string;
  hint?: string;
  options?: { value: string; label: string }[];
  /** Save the (pill/text) value as a number. */
  numeric?: boolean;
  /**
   * Choices come from the operator's records. 'choice' = pick one (pills when
   * there are few, a dropdown when many); 'suggest' = free text with suggestions.
   * With no data yet the field falls back to plain text.
   */
  choices?: OptionField;
  control?: 'choice' | 'suggest';
  secure?: boolean; // sensitive (ת"ז / passport / bank): stored encrypted, sent only when changed
  required?: boolean;
  validate?: (v: string) => string | null; // returns an error message or null
  /** Shown (and validated/saved) only when this returns true. */
  showIf?: (form: Record<string, string>) => boolean;
  /** UI-only choice, never sent to the server. */
  local?: boolean;
  /** Shown, never edited or sent (the office changes it). */
  readOnly?: boolean;
}
interface Step {
  title: string;
  intro?: string; // short line under the title
  reassure?: string; // trust block (sensitive steps)
  optional?: boolean; // shows a "skip for now"
  documentsStep?: boolean; // renders the private document uploads instead of fields
  identityStep?: boolean; // ת"ז or passport: complete when either is on file
  /** ID, passport, documents or bank: not offered to someone only looking (the office invites first). */
  sensitive?: boolean;
  fields: Field[];
}

const DOC_SLOTS: { type: ResidentDocType; label: string; hint: string }[] = [
  { type: 'enrollment_certificate', label: 'אישור לימודים', hint: 'תמונה ברורה של האישור' },
  { type: 'id_card', label: 'תעודת זהות', hint: 'הצד עם התמונה' },
  { type: 'id_appendix', label: 'ספח תעודת זהות', hint: 'הדף עם הפרטים' },
];

const digits = (v: string) => v.replace(/\D/g, '');
const validId = (v: string) => (/^\d{9}$/.test(digits(v)) ? null : 'יש להזין 9 ספרות');
const validPhone = (v: string) => (digits(v).length >= 9 ? null : 'מספר טלפון קצר מדי');

// Lists of up to this many options are shown in full (one tap); longer ones open
// the searchable picker. One rule, two patterns.
const MAX_VISIBLE_OPTIONS = 7;
// Short lists read best in their natural order, not by popularity.
const DEGREE_ORDER = ['מכינה', 'תואר ראשון', 'תואר שני', 'תואר שלישי', 'פוסט דוקטורט', 'עתודה'];
function naturalOrder(field: OptionField, values: string[]): string[] {
  if (field !== 'degreeType') return values;
  const rank = (v: string) => (DEGREE_ORDER.includes(v) ? DEGREE_ORDER.indexOf(v) : DEGREE_ORDER.length);
  return [...values].sort((a, b) => rank(a) - rank(b));
}
const visible = (f: Field, form: Record<string, string>) => !f.showIf || f.showIf(form);

/** The form as the server knows it (drafts and restores compare against this). */
function baselineOf(profile: Profile): Record<string, string> {
  return {
    firstName: profile.firstName ?? '',
    lastName: profile.lastName ?? '',
    email: profile.email ?? '',
    gender: profile.gender && profile.gender !== 'UNKNOWN' ? profile.gender : '',
    dateOfBirth: profile.dateOfBirth ?? '',
    phone: profile.phone ?? '',
    whatsappPhone: profile.whatsappPhone ?? '',
    homeAddress: profile.homeAddress ?? '',
    emergencyContactName: profile.emergencyContactName ?? '',
    emergencyContactPhone: profile.emergencyContactPhone ?? '',
    emergencyContactRelation: profile.emergencyContactRelation ?? '',
    studyInstitution: profile.studyInstitution ?? '',
    faculty: profile.faculty ?? '',
    degreeType: profile.degreeType ?? '',
    studyYear: profile.studyYear != null ? String(profile.studyYear) : '',
    studyStartDate: profile.studyStartDate ?? '',
    studyEndDate: profile.studyEndDate ?? '',
    bankName: profile.bankName ?? '',
    bankBranch: profile.bankBranch ?? '',
    nationality: profile.nationality ?? '',
    // Never pre-filled: only the last 4 digits come back ("on file: ••••1234").
    nationalId: '',
    passportNumber: '',
    bankAccount: '',
    idMethod: !profile.hasNationalId && profile.hasPassport ? 'passport' : 'id',
  };
}

// Unsaved typing survives a dropped connection or a reload, per person, on this
// device only. Sensitive numbers (ID, passport, bank) are never written here.
const SECURE_KEYS = new Set(['nationalId', 'passportNumber', 'bankAccount']);
// Only what the resident changed and hasn't saved yet; dropped after 7 days and
// on sign-out (a shared phone must not keep someone's details).
export const DRAFT_PREFIX = 'rooms_draft_v1_';
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const draftKey = (personId: string) => `${DRAFT_PREFIX}${personId}`;
type DraftEntry = { v: string; base: string };
/**
 * The unsaved changes still worth restoring: a field the office (or another
 * device) changed since it was typed is dropped, so an old draft never
 * overwrites newer data.
 */
function readDraft(personId: string, server: Record<string, string>): Record<string, string> | null {
  try {
    const raw = localStorage.getItem(draftKey(personId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { at?: number; values?: Record<string, DraftEntry> };
    if (!parsed.at || Date.now() - parsed.at > DRAFT_TTL_MS || !parsed.values) {
      localStorage.removeItem(draftKey(personId));
      return null;
    }
    const keep = Object.entries(parsed.values).filter(
      ([k, e]) => e && typeof e.v === 'string' && (server[k] ?? '') === e.base && e.v !== e.base,
    );
    return keep.length ? Object.fromEntries(keep.map(([k, e]) => [k, e.v])) : null;
  } catch {
    return null;
  }
}
function writeDraft(personId: string, values: Record<string, string>, baseline: Record<string, string>) {
  try {
    const changed = Object.fromEntries(
      Object.entries(values)
        .filter(([k, v]) => !SECURE_KEYS.has(k) && v !== (baseline[k] ?? ''))
        .map(([k, v]) => [k, { v, base: baseline[k] ?? '' } satisfies DraftEntry]),
    );
    if (Object.keys(changed).length === 0) localStorage.removeItem(draftKey(personId));
    else localStorage.setItem(draftKey(personId), JSON.stringify({ at: Date.now(), values: changed }));
  } catch {
    /* private mode or full storage: typing still works, just not kept */
  }
}
function clearDraft(personId: string) {
  try {
    localStorage.removeItem(draftKey(personId));
  } catch {
    /* ignore */
  }
}

const STEPS: Step[] = [
  {
    title: 'פרטים אישיים',
    fields: [
      { key: 'firstName', label: 'שם פרטי', required: true },
      { key: 'lastName', label: 'שם משפחה', required: true },
      {
        key: 'gender',
        label: 'מגדר',
        type: 'pills',
        options: [
          { value: 'FEMALE', label: 'נקבה' },
          { value: 'MALE', label: 'זכר' },
          { value: 'OTHER', label: 'אחר' },
        ],
      },
      { key: 'dateOfBirth', label: 'תאריך לידה', type: 'date' },
    ],
  },
  {
    // One identification method, chosen first (as Electra does), and required:
    // the lease can't be signed without it.
    title: 'תעודת זהות / דרכון',
    identityStep: true,
    sensitive: true,
    intro: 'נדרש לרישום ולחתימה על חוזה השכירות.',
    reassure: 'מספרי הזהות והדרכון נשמרים מוצפנים. רק את/ה וצוות הדיור רואים אותם, והם לא נשלחים לאף גורם אחר.',
    fields: [
      {
        key: 'idMethod',
        label: 'אמצעי זיהוי',
        type: 'pills',
        local: true,
        required: true,
        options: [
          { value: 'id', label: 'תעודת זהות ישראלית' },
          { value: 'passport', label: 'דרכון' },
        ],
      },
      {
        key: 'nationalId',
        label: 'מספר תעודת זהות',
        type: 'tel',
        placeholder: '9 ספרות',
        secure: true,
        required: true,
        validate: validId,
        showIf: (f) => f.idMethod === 'id',
      },
      {
        key: 'passportNumber',
        label: 'מספר דרכון',
        type: 'text',
        secure: true,
        required: true,
        showIf: (f) => f.idMethod === 'passport',
      },
      { key: 'nationality', label: 'אזרחות', required: true, showIf: (f) => f.idMethod === 'passport' },
    ],
  },
  {
    title: 'פרטי קשר',
    fields: [
      {
        key: 'email',
        label: 'אימייל',
        type: 'text',
        required: true,
        readOnly: true,
        hint: 'לשינוי המייל, פנו לצוות הדיור',
      },
      { key: 'phone', label: 'טלפון נייד', type: 'tel', required: true, validate: validPhone },
      { key: 'whatsappPhone', label: 'וואטסאפ', hint: 'רק אם שונה מהנייד', type: 'tel' },
      { key: 'homeAddress', label: 'כתובת מגורים קבועה' },
    ],
  },
  {
    title: 'איש קשר לחירום',
    intro: 'מי שנוכל לפנות אליו במקרה הצורך.',
    fields: [
      { key: 'emergencyContactName', label: 'שם' },
      {
        key: 'emergencyContactPhone',
        label: 'טלפון',
        type: 'tel',
        validate: (v) => (digits(v).length >= 9 ? null : 'מספר קצר מדי'),
      },
      { key: 'emergencyContactRelation', label: 'קרבה', hint: 'הורה, בן/בת זוג, חבר/ה…' },
    ],
  },
  {
    title: 'פרטי לימודים',
    intro: 'תקופת הלימודים עוזרת להתאים את תקופת המגורים.',
    fields: [
      { key: 'studyInstitution', label: 'מוסד לימודים', choices: 'studyInstitution', control: 'suggest' },
      { key: 'degreeType', label: 'סוג תואר', choices: 'degreeType', control: 'choice' },
      { key: 'faculty', label: 'פקולטה / חוג', choices: 'faculty', control: 'choice' },
      {
        // Year WITHIN the degree (1st, 2nd...), not the calendar year: pills so
        // "2026" can't be typed by mistake.
        key: 'studyYear',
        label: 'שנה בתואר',
        hint: 'לא שנה קלנדרית',
        type: 'pills',
        numeric: true,
        options: [
          { value: '0', label: 'מכינה' },
          { value: '1', label: 'א׳' },
          { value: '2', label: 'ב׳' },
          { value: '3', label: 'ג׳' },
          { value: '4', label: 'ד׳' },
          { value: '5', label: 'ה׳' },
          { value: '6', label: 'ו׳+' },
        ],
      },
      { key: 'studyStartDate', label: 'תחילת לימודים', type: 'date' },
      { key: 'studyEndDate', label: 'סיום לימודים (משוער)', type: 'date' },
    ],
  },
  {
    title: 'מסמכים',
    intro: 'צילום ברור וקריא של כל מסמך. אפשר לצלם או להעלות מהגלריה.',
    reassure: 'המסמכים נשמרים מוצפנים. רק את/ה וצוות הדיור יכולים לצפות בהם.',
    optional: true,
    documentsStep: true,
    sensitive: true,
    fields: [],
  },
  {
    title: 'פרטי בנק',
    sensitive: true,
    intro: 'לגביית שכר הדירה.',
    reassure: 'מספר החשבון משמש לגבייה בלבד ונשמר מוצפן.',
    optional: true,
    fields: [
      { key: 'bankName', label: 'שם הבנק', choices: 'bankName', control: 'choice' },
      {
        key: 'bankBranch',
        label: 'מספר סניף',
        type: 'tel',
        validate: (v) => (/^\d{1,4}$/.test(v.trim()) ? null : 'מספר סניף: עד 4 ספרות'),
      },
      {
        key: 'bankAccount',
        label: 'מספר חשבון',
        type: 'tel',
        secure: true,
        validate: (v) => (digits(v).length >= 3 ? null : 'מספר חשבון קצר מדי'),
      },
    ],
  },
];

type FormState = Record<string, string>;

export function Wizard() {
  const { profile, room, hydrate, save, logout, error: authError, loading: authLoading } = useAuth();
  // Someone only looking (came in through the front door, not invited yet) is not asked for ID or bank details.
  const steps = useMemo(() => (profile?.prospect ? STEPS.filter((st) => !st.sensitive) : STEPS), [profile?.prospect]);
  const [phase, setPhase] = useState<'welcome' | 'home' | 'form' | 'done'>('welcome');
  const phaseChosen = useRef(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [restored, setRestored] = useState(false);
  // The server's values when the form was loaded: drafts store only changes from these.
  const baselineRef = useRef<Record<string, string>>({});
  const [failedUpload, setFailedUpload] = useState<{ docType: ResidentDocType; file: File } | null>(null);
  const confirmRef = useRef<HTMLDivElement | null>(null);
  // The panel opens below the fields: bring it into view so it is not missed.
  useEffect(() => {
    if (confirming) confirmRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [confirming]);
  const [saved, setSaved] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const savedTimer = useRef<ReturnType<typeof setTimeout>>();
  const [docs, setDocs] = useState<Partial<Record<string, ResidentDoc>>>({});
  const [docUploading, setDocUploading] = useState<string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  const [options, setOptions] = useState<FieldOptions | null>(null);

  // Best effort: without options the choice fields simply stay free text.
  useEffect(() => {
    if (!profile || options) return;
    api
      .options()
      .then(setOptions)
      .catch(() => setOptions({ degreeType: [], faculty: [], studyInstitution: [], bankName: [] }));
  }, [profile, options]);

  useEffect(() => {
    if (!profile) void hydrate();
  }, [profile, hydrate]);

  useEffect(() => {
    if (!profile) return;
    // Office records loaded with only a full name: offer it split into the two
    // fields (a suggestion in the form; saved only when the resident continues).
    const base = baselineOf(profile);
    const full = profile.fullName?.trim();
    if (!base.firstName && !base.lastName && full) {
      const [first, ...rest] = full.split(/\s+/);
      setForm({ ...base, firstName: first ?? '', lastName: rest.join(' ') });
    } else {
      setForm(base);
    }
    // Typing that never reached the server (lost connection, closed tab) comes back.
    baselineRef.current = baselineOf(profile);
    const draft = readDraft(profile.id, baselineRef.current);
    if (draft && Object.keys(draft).length) {
      setForm((f) => ({ ...f, ...draft }));
      setRestored(true);
    }
  }, [profile]);

  // A resident who already registered lands on their home, not on "let's register".
  useEffect(() => {
    if (!profile || phaseChosen.current) return;
    phaseChosen.current = true;
    // ?field=homeAddress (from the lease page) opens the step that holds that field.
    const field = new URLSearchParams(window.location.search).get('field');
    const target = field ? steps.findIndex((st) => st.fields.some((f) => f.key === field)) : -1;
    if (target >= 0) {
      setStep(target);
      setPhase('form');
      return;
    }
    // A prospect from the portal's front door lands on home (the tour card), not on the move-in registration.
    if (profile.activated || profile.prospect) setPhase('home');
  }, [profile, steps]);

  // Load any documents already uploaded, so the slots show their status.
  useEffect(() => {
    if (!profile) return;
    api
      .listDocs()
      .then((r) => {
        const map: Record<string, ResidentDoc> = {};
        for (const d of r.documents) if (d.docType) map[d.docType] = d;
        setDocs(map);
      })
      .catch(() => {});
  }, [profile]);

  useEffect(() => {
    if (phase === 'done' && typeof navigator !== 'undefined') navigator.vibrate?.([28, 50, 28]);
  }, [phase]);

  // Two-stage upload: compress → signed PUT to the private bucket → record it.
  async function uploadDoc(docType: ResidentDocType, file: File) {
    setDocError(null);
    if (!isValidType(file)) {
      setDocError('יש להעלות תמונה או PDF בלבד');
      return;
    }
    if (!isValidSize(file)) {
      setDocError('הקובץ גדול מדי (עד 10MB)');
      return;
    }
    setDocUploading(docType);
    try {
      const prepared = await prepareForUpload(file);
      const { uploadUrl, storageKey } = await api.docUploadUrl(docType, prepared.type, prepared.size);
      await putToStorage(uploadUrl, prepared);
      const doc = await api.createDoc(docType, storageKey);
      setDocs((d) => ({ ...d, [docType]: doc }));
      setFailedUpload(null);
    } catch (e) {
      setDocError((e as Error).message);
      // Keep the chosen file so "try again" doesn't need the camera a second time.
      setFailedUpload({ docType, file });
    } finally {
      setDocUploading(null);
    }
  }

  const total = steps.length;
  const current = steps[Math.min(step, total - 1)]!;
  const roomLabel = useMemo(
    () => (room ? [room.building, room.apartment ? `דירה ${room.apartment}` : null].filter(Boolean).join(' · ') : null),
    [room],
  );
  // The building's brand (null keeps TendedLoop's look) and its move-in welcome.
  const brand = useBrand((s) => s.brand);
  // Only with this session's brand: a cached one may belong to someone else's building.
  const brandLoaded = useBrand((s) => s.loaded);
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [tourState, setTourState] = useState<string | null>(null);
  useEffect(() => {
    if (phase === 'home' && brandLoaded && shouldWelcome(brand, room)) setWelcomeOpen(true);
  }, [phase, brand, brandLoaded, room]);
  // Home, welcome and the finish open under the building's full band; a
  // registration step keeps the slim bar (h-14) so the form and its action stay
  // in view. The screen below a band is that much shorter.
  const band = brand && <BrandBand brand={brand} size="page" />;
  const barBand = brand && <BrandBand brand={brand} size="bar" />;
  const fullHeight = brand ? 'min-h-[calc(100vh-12rem)]' : 'min-h-screen';
  const formHeight = brand ? 'min-h-[calc(100vh-3.5rem)]' : 'min-h-screen';

  /** Where they live: on the building's photo when it has one, else a plain chip. */
  const roomCard = (spacing: string) =>
    roomLabel &&
    (brand?.heroImageUrl ? (
      <div className={`relative h-36 overflow-hidden rounded-2xl ${spacing}`}>
        <img src={brand.heroImageUrl} alt="" className="h-full w-full object-cover" />
        {brand.heroIsRender && (
          <span className="absolute end-2.5 top-2.5 rounded-md bg-black/60 px-2 py-0.5 text-[14px] text-white">
            הדמיה
          </span>
        )}
        <div className="absolute bottom-2.5 start-2.5 flex items-center gap-1.5 rounded-full bg-black px-3 py-1.5 text-[15px] font-medium text-white">
          <Home className="h-4 w-4" aria-hidden />
          <span>{roomLabel}</span>
        </div>
      </div>
    ) : (
      <div
        className={`flex items-center gap-2 rounded-xl bg-brand-50 px-4 py-3 text-[15px] font-medium text-brand-700 ${spacing}`}
      >
        <Home className="h-4 w-4" />
        <span>{roomLabel}</span>
      </div>
    ));

  // Each step's state, by the same rules the office's portal status uses:
  // a step is "missing" only when something required is not there yet.
  const stepRows = (() => {
    if (!profile) return [];
    // Saved values only: a restored, unsaved draft doesn't make a step "done".
    const filled = (k: string) => !!(baselineRef.current[k] ?? '').trim();
    const docCount = DOC_SLOTS.filter((d) => docs[d.type]).length;
    return steps.map((st, i) => {
      let state: 'done' | 'missing' | 'empty' = 'empty';
      let note = '';
      if (st.documentsStep) {
        state = docCount === DOC_SLOTS.length ? 'done' : 'missing';
        note = state === 'done' ? 'כל המסמכים הועלו' : `הועלו ${docCount} מתוך ${DOC_SLOTS.length}`;
      } else if (st.identityStep) {
        state = profile.hasNationalId || profile.hasPassport ? 'done' : 'missing';
        note = state === 'done' ? (profile.hasNationalId ? 'תעודת זהות' : 'דרכון') : 'חסר: תעודת זהות או דרכון';
      } else {
        const required = st.fields.filter((f) => f.required && !f.local);
        const any = st.fields.some((f) => !f.local && filled(f.key));
        if (required.length) {
          const missing = required.filter((f) => !filled(f.key));
          state = missing.length ? 'missing' : 'done';
          note = missing.length ? `חסר: ${missing.map((f) => f.label).join(', ')}` : 'הושלם';
        } else {
          state = any ? 'done' : 'empty';
          note = any ? 'הושלם' : st.optional ? 'לא חובה' : 'לא מולא';
        }
      }
      return { i, title: st.title, state, note };
    });
  })();
  const missingRows = stepRows.filter((r) => r.state === 'missing');
  const firstMissing = missingRows[0];
  const resumeAt = (i: number) => {
    setStep(i);
    setPhase('form');
  };

  // "••••1234" when a sensitive value is already on file, else null.
  const storedMask = (key: string): string | null =>
    !profile
      ? null
      : key === 'nationalId'
        ? profile.nationalIdMasked
        : key === 'passportNumber'
          ? profile.passportMasked
          : key === 'bankAccount'
            ? profile.bankAccountMasked
            : null;

  const setField = (key: string, value: string) => {
    setForm((f) => {
      const next = { ...f, [key]: value };
      if (profile) writeDraft(profile.id, next, baselineRef.current);
      return next;
    });
    if (errors[key]) setErrors((e) => ({ ...e, [key]: '' }));
  };

  function validateStep(): boolean {
    const next: Record<string, string> = {};
    for (const f of current.fields) {
      if (!visible(f, form) || f.readOnly) continue;
      const v = (form[f.key] ?? '').trim();
      if (f.required && !v && !storedMask(f.key)) next[f.key] = 'שדה חובה';
      else if (v && f.validate) {
        const e = f.validate(v);
        if (e) next[f.key] = e;
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function persistStep(): Promise<boolean> {
    const patch: ProfilePatch = {};
    for (const f of current.fields) {
      if (f.local || f.readOnly || !visible(f, form)) continue;
      const raw = (form[f.key] ?? '').trim();
      // Empty = keep what is on file (a sensitive field is only sent when typed).
      if (!raw) continue;
      if (f.type === 'number' || f.numeric) (patch as Record<string, unknown>)[f.key] = Number(raw);
      else (patch as Record<string, unknown>)[f.key] = raw;
    }
    if (Object.keys(patch).length === 0) {
      // Nothing to send: any draft is either saved already or not wanted.
      if (profile) clearDraft(profile.id);
      setRestored(false);
      return true;
    }
    setSaving(true);
    setServerError(null);
    try {
      await save(patch);
      setSaved(true);
      if (profile) clearDraft(profile.id);
      setRestored(false);
      clearTimeout(savedTimer.current);
      savedTimer.current = setTimeout(() => setSaved(false), 1600);
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.code === REAUTH_CODE) {
        // Changing an ID or bank number needs a recent sign-in: confirm, then save again.
        setConfirming(true);
        return false;
      }
      // Server validation comes back as "<field>: <English zod text>". Pin it to
      // the field in Hebrew instead of showing the raw message.
      const msg = (e as Error).message;
      const field = /^([A-Za-z]+):/.exec(msg)?.[1];
      if (field && current.fields.some((f) => f.key === field)) {
        setErrors((prev) => ({ ...prev, [field]: 'הערך אינו תקין' }));
        setServerError('יש לתקן את השדה המסומן');
      } else {
        setServerError(/[\u0590-\u05FF]/.test(msg) ? msg : 'השמירה נכשלה. נסו שוב.');
      }
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function advance(skip = false) {
    if (skip && profile) {
      // Skipping means "not now": don't keep typed values to restore later.
      clearDraft(profile.id);
      setRestored(false);
    }
    if (!skip && !validateStep()) return;
    if (!skip && !(await persistStep())) return;
    setErrors({});
    if (step < total - 1) setStep((s) => s + 1);
    else setPhase('done');
  }

  if (!profile) {
    return (
      <>
        {band}
        <div className={`flex ${fullHeight} flex-col items-center justify-center gap-4 px-5 text-center`}>
          {authError && !authLoading ? (
            <>
              <p className="text-[16px] leading-relaxed text-slate-700" role="alert">
                {authError}
              </p>
              <button
                type="button"
                onClick={() => void hydrate()}
                className="min-h-[48px] rounded-xl bg-brand-500 px-6 text-[16px] font-semibold text-white"
              >
                נסו שוב
              </button>
            </>
          ) : (
            <Loader2 className="h-7 w-7 animate-spin text-brand-500" />
          )}
        </div>
      </>
    );
  }

  // ── Welcome ────────────────────────────────────────────────────────────────
  if (phase === 'welcome') {
    return (
      <>
        {band}
        <div className={`mx-auto flex max-w-md flex-col px-5 pb-6 ${fullHeight} ${brand ? 'pt-2' : 'pt-14'}`}>
          {!brand && (
            <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-500 text-white shadow-lg">
              <Home className="h-8 w-8" />
            </div>
          )}
          <h1 className="mb-2 text-2xl font-bold text-brand-900">
            {greetName(profile) ? `שלום ${greetName(profile)},` : 'שלום,'}
          </h1>
          <p className="text-[16px] leading-relaxed text-slate-600">
            נשלים יחד את פרטי ההרשמה למגורים. אפשר לעצור ולחזור בכל שלב.
          </p>

          {roomCard('mt-5')}

          <div className="mt-6 space-y-3">
            <div className="flex items-center gap-3 text-[15px] text-slate-600">
              <Clock className="h-5 w-5 shrink-0 text-brand-500" /> כ-5 דקות, ‎{total} שלבים קצרים
            </div>
            <div className="flex items-start gap-3 text-[15px] text-slate-600">
              <ListChecks className="mt-0.5 h-5 w-5 shrink-0 text-brand-500" />
              <span>כדאי להכין: תעודת זהות או דרכון, אישור לימודים ופרטי חשבון בנק</span>
            </div>
            <div className="flex items-center gap-3 text-[15px] text-slate-600">
              <ShieldCheck className="h-5 w-5 shrink-0 text-brand-500" /> הפרטים נשמרים מוצפנים ומאובטחים
            </div>
          </div>

          <div className="mt-auto pt-8">
            <button
              onClick={() => setPhase('form')}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand-500 text-[16px] font-semibold text-white shadow-md transition-colors hover:bg-brand-600 active:scale-[0.99]"
            >
              בואו נתחיל
              <ChevronLeft className="h-5 w-5" />
            </button>
            {/* A prospect can book a tour before registering: a quiet second option. */}
            <PortalTourCard variant="welcome" />
          </div>
        </div>
      </>
    );
  }

  // ── Home (returning resident) ──────────────────────────────────────────────
  if (phase === 'home') {
    const rows = stepRows;
    // Still only looking (came in through the front door): no move-in registration on home yet.
    const looking = !!profile.prospect;
    const missingCount = missingRows.length;

    return (
      <>
        {band}
        {welcomeOpen && brand && room && (
          <WelcomeHome brand={brand} room={room} onClose={() => setWelcomeOpen(false)} />
        )}
        <div className={'mx-auto min-h-screen max-w-md px-5 pb-10 ' + (brand ? 'pt-2' : 'pt-10')}>
          <h1 className="text-2xl font-bold text-brand-900">
            {greetName(profile) ? `שלום ${greetName(profile)}` : 'שלום'}
          </h1>
          {roomCard('mt-3')}

          {/* The one primary action: a tour to book (before or after signing), else the lease. */}
          <PortalTourCard onState={setTourState} />
          <NextStep secondary={tourState === 'invite'} />

          {firstMissing && !looking && (
            <button
              type="button"
              onClick={() => resumeAt(firstMissing.i)}
              className="mt-4 flex min-h-[52px] w-full items-center justify-between gap-3 rounded-2xl border-2 border-brand-500 bg-white px-5 py-3 text-start text-[16px] font-semibold text-brand-700"
            >
              <span>
                המשך מאיפה שעצרת
                <span className="block text-[15px] font-normal text-slate-600">{firstMissing.title}</span>
              </span>
              <Chevron className="h-5 w-5 shrink-0" />
            </button>
          )}

          {!looking && (
            <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-4" aria-labelledby="reg-title">
              <div className="flex items-center justify-between">
                <h2 id="reg-title" className="text-[17px] font-bold text-brand-900">
                  פרטי ההרשמה
                </h2>
                <span className={'text-[15px] font-medium ' + (missingCount ? 'text-amber-700' : 'text-brand-600')}>
                  {missingCount === 0
                    ? 'ההרשמה הושלמה'
                    : missingCount === 1
                      ? 'חסר פריט אחד'
                      : `חסרים ${missingCount} פריטים`}
                </span>
              </div>
              <ul className="mt-2 divide-y divide-slate-100">
                {rows.map((r) => (
                  <li key={r.i}>
                    <button
                      type="button"
                      onClick={() => {
                        setStep(r.i);
                        setPhase('form');
                      }}
                      className="flex min-h-[56px] w-full items-center gap-3 py-2 text-start"
                    >
                      {r.state === 'done' ? (
                        <CheckCircle2 className="h-5 w-5 shrink-0 text-done" />
                      ) : r.state === 'missing' ? (
                        <AlertCircle className="h-5 w-5 shrink-0 text-amber-600" />
                      ) : (
                        <span className="h-5 w-5 shrink-0 rounded-full border-2 border-slate-300" aria-hidden />
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block text-[16px] font-medium text-brand-900">{r.title}</span>
                        <span
                          className={
                            'block text-[15px] ' + (r.state === 'missing' ? 'text-amber-700' : 'text-slate-500')
                          }
                        >
                          {r.note}
                        </span>
                      </span>
                      <Chevron className="h-5 w-5 shrink-0 text-slate-400" />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <WhatsIncluded brand={brand} />
          <OfficeCard />
          <button onClick={logout} className="mt-6 min-h-[44px] w-full text-center text-[15px] text-slate-600">
            יציאה
          </button>
        </div>
      </>
    );
  }

  // ── Done ───────────────────────────────────────────────────────────────────
  if (phase === 'done') {
    return (
      <>
        {band}
        <div
          className={`mx-auto flex ${fullHeight} max-w-md flex-col items-center justify-center px-6 text-center ${brand ? 'py-6' : ''}`}
        >
          <div className="mb-5 flex h-20 w-20 animate-[pop_.4s_ease-out] items-center justify-center rounded-full bg-brand-50 text-brand-500">
            <PartyPopper className="h-10 w-10" />
          </div>
          {/* Honest about what is still missing: skipped steps are not "done". */}
          <h1 className="mb-2 text-2xl font-bold text-brand-900">
            {missingRows.length ? 'שמרנו את מה שמילאת' : 'תודה, סיימנו!'}
          </h1>
          <p className="text-[16px] leading-relaxed text-slate-600">
            {missingRows.length
              ? `נשארו עוד ${missingRows.length === 1 ? 'פריט אחד' : `${missingRows.length} פריטים`}: ${missingRows.map((r) => r.title).join(', ')}. אפשר להשלים עכשיו או בפעם הבאה.`
              : 'כל הפרטים נשמרו, וצוות הדיור רואה אותם.'}
          </p>
          {firstMissing && (
            <button
              type="button"
              onClick={() => resumeAt(firstMissing.i)}
              className="mt-5 min-h-[52px] w-full rounded-2xl bg-brand-500 px-5 text-[16px] font-semibold text-white"
            >
              להשלמת {firstMissing.title}
            </button>
          )}
          <NextStep />
          <div className="mt-5 flex items-center gap-2 rounded-xl bg-brand-50 px-4 py-2.5 text-[15px] font-medium text-brand-700">
            <Lock className="h-4 w-4" /> הפרטים שלך מאובטחים ומוצפנים
          </div>
          <button
            onClick={() => setPhase('home')}
            className="mt-8 flex min-h-[44px] items-center gap-1.5 text-[15px] font-semibold text-brand-700 hover:underline"
          >
            <Home className="h-4 w-4" /> לעמוד הבית
          </button>
          <button onClick={logout} className="mt-3 min-h-[44px] text-[15px] text-slate-500">
            יציאה
          </button>
          <style>{`@keyframes pop{0%{transform:scale(.6);opacity:0}60%{transform:scale(1.08)}100%{transform:scale(1);opacity:1}}`}</style>
        </div>
      </>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────
  const progress = ((step + 1) / total) * 100;
  const isLast = step === total - 1;
  const sensitive = current.fields.some((f) => f.secure) || !!current.documentsStep;

  return (
    <>
      {barBand}
      <div className={`mx-auto flex ${formHeight} max-w-md flex-col`}>
        {/* Header / progress */}
        <div className={'px-5 ' + (brand ? 'pt-4' : 'pt-6')}>
          {profile.activated && (
            <button
              type="button"
              onClick={async () => {
                if (await persistStep()) setPhase('home');
              }}
              className="mb-2 flex min-h-[44px] items-center gap-1 text-[15px] font-medium text-brand-700"
            >
              <ChevronRight className="h-4 w-4" /> שמירה וחזרה לעמוד הבית
            </button>
          )}
          <div className="mb-2 flex items-center justify-between text-[15px] text-slate-500">
            <span>
              שלב {step + 1} מתוך {total}
            </span>
            {sensitive ? (
              <span className="flex items-center gap-1 text-brand-600">
                <Lock className="h-3.5 w-3.5" /> מוצפן
              </span>
            ) : (
              roomLabel && <span className="truncate">{roomLabel}</span>
            )}
          </div>
          <div className={'h-2 overflow-hidden rounded-full ' + (brand ? 'bg-brand-100' : 'bg-slate-200')}>
            <div
              className="h-full rounded-full bg-brand-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 px-5 pt-5">
          <h1 className="text-2xl font-bold text-brand-900">{current.title}</h1>
          {current.intro && <p className="mt-1 text-[15px] text-slate-500">{current.intro}</p>}
          {restored && (
            <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[15px] text-amber-900" role="status">
              שחזרנו את מה שהקלדת ועוד לא נשמר. לחצו "המשך" כדי לשמור.
            </p>
          )}

          {current.reassure && (
            <div className="mt-4 flex gap-2.5 rounded-xl border border-brand-100 bg-brand-50/70 p-3.5">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-500" />
              <p className="text-[15px] leading-relaxed text-brand-900/80">{current.reassure}</p>
            </div>
          )}

          {current.documentsStep && (
            <div className="mt-5 space-y-3">
              {DOC_SLOTS.map((slot) => {
                const doc = docs[slot.type];
                const busy = docUploading === slot.type;
                return (
                  <div key={slot.type} className="rounded-xl border border-slate-200 bg-white p-3.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[15px] font-medium text-brand-900">{slot.label}</div>
                        <div className="text-[15px] text-slate-400">{slot.hint}</div>
                      </div>
                      {doc && (
                        <span className="flex shrink-0 items-center gap-1 text-[15px] font-medium text-brand-600">
                          <Check className="h-3.5 w-3.5" /> הועלה
                        </span>
                      )}
                    </div>

                    {doc?.readUrl && (
                      <>
                        {/* Show what was uploaded, uncropped. A PDF has no image preview, so the link below opens it. */}
                        <img
                          src={doc.readUrl}
                          alt={slot.label}
                          className="mt-3 max-h-56 w-full rounded-lg border border-slate-100 bg-slate-50 object-contain"
                          onError={(e) => {
                            (e.currentTarget as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <a
                          href={doc.readUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 flex min-h-[44px] items-center gap-1.5 text-[15px] font-medium text-brand-700"
                        >
                          <FileText className="h-4 w-4" /> צפייה במסמך שהועלה
                        </a>
                      </>
                    )}

                    <label
                      className={
                        'mt-3 flex h-11 cursor-pointer items-center justify-center gap-2 rounded-xl border text-[15px] font-medium transition-colors ' +
                        (doc
                          ? 'border-slate-200 text-slate-500 hover:border-brand-300'
                          : 'border-dashed border-brand-300 bg-brand-50/40 text-brand-700')
                      }
                    >
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        capture="environment"
                        className="hidden"
                        disabled={busy}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          e.currentTarget.value = '';
                          if (f) void uploadDoc(slot.type, f);
                        }}
                      />
                      {busy ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : doc ? (
                        <>
                          <FileText className="h-4 w-4" /> החלפת הקובץ
                        </>
                      ) : (
                        <>
                          <Camera className="h-5 w-5" /> צילום / העלאה
                        </>
                      )}
                    </label>
                  </div>
                );
              })}
              {docError && (
                <div
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-red-50 px-3 py-2 text-[15px] text-red-600"
                  role="alert"
                >
                  <span>{docError}</span>
                  {failedUpload && (
                    <button
                      type="button"
                      onClick={() => void uploadDoc(failedUpload.docType, failedUpload.file)}
                      className="min-h-[44px] rounded-lg border border-red-200 bg-white px-3 font-semibold text-red-700"
                    >
                      נסו שוב
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="mt-5 space-y-4">
            {current.fields
              .filter((f) => visible(f, form))
              .map((field) => {
                const err = errors[field.key];

                return (
                  <div key={field.key}>
                    <div className="mb-1.5 flex items-baseline justify-between">
                      <label htmlFor={`f-${field.key}`} className="text-[15px] font-medium text-brand-900">
                        {field.label}
                        {field.required && <span className="text-brand-500"> *</span>}
                      </label>
                      {field.hint && <span className="text-[15px] text-slate-400">{field.hint}</span>}
                    </div>

                    {(() => {
                      // Resolve how a data-driven field renders right now.
                      if (!field.choices) return null;
                      const cur = form[field.key] ?? '';
                      const vals = options?.[field.choices] ?? [];
                      const list = cur && !vals.includes(cur) ? [cur, ...vals] : vals;
                      if (!vals.length) return null; // no data yet: plain text input below
                      // Long lists, and any list that also takes free text: the searchable sheet.
                      if (field.control === 'suggest' || list.length > MAX_VISIBLE_OPTIONS) {
                        return (
                          <ChoicePicker
                            label={field.label}
                            value={cur}
                            options={list}
                            allowCustom={field.control === 'suggest'}
                            invalid={!!err}
                            onChange={(v) => setField(field.key, v)}
                          />
                        );
                      }
                      // Short lists: every option visible, one tap.
                      return (
                        <div className="grid grid-cols-2 gap-2">
                          {naturalOrder(field.choices, list).map((v) => {
                            const active = cur === v;
                            return (
                              <button
                                key={v}
                                type="button"
                                onClick={() => setField(field.key, active ? '' : v)}
                                aria-pressed={active}
                                className={
                                  'min-h-12 rounded-xl border px-2 py-2 text-[15px] font-medium transition-colors ' +
                                  (active
                                    ? 'border-brand-500 bg-brand-500 text-white'
                                    : 'border-slate-200 bg-white text-brand-900 hover:border-brand-300')
                                }
                              >
                                {v}
                              </button>
                            );
                          })}
                        </div>
                      );
                    })() ??
                      (field.type === 'pills' ? (
                        <div className={field.options!.length > 4 ? 'grid grid-cols-4 gap-2' : 'flex gap-2'}>
                          {field.options!.map((o) => {
                            const active = form[field.key] === o.value;
                            return (
                              <button
                                key={o.value}
                                type="button"
                                onClick={() => setField(field.key, active ? '' : o.value)}
                                aria-pressed={active}
                                className={
                                  'min-h-12 flex-1 px-2 rounded-xl border text-[15px] font-medium transition-colors ' +
                                  (active
                                    ? 'border-brand-500 bg-brand-500 text-white'
                                    : 'border-slate-200 bg-white text-brand-900 hover:border-brand-300')
                                }
                              >
                                {o.label}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <input
                          id={`f-${field.key}`}
                          aria-invalid={err ? true : undefined}
                          aria-describedby={err ? `f-${field.key}-err` : undefined}
                          type={field.type ?? 'text'}
                          inputMode={field.type === 'tel' || field.type === 'number' ? 'numeric' : undefined}
                          value={form[field.key] ?? ''}
                          readOnly={field.readOnly}
                          dir={field.readOnly && field.key === 'email' ? 'ltr' : undefined}
                          onChange={(e) => !field.readOnly && setField(field.key, e.target.value)}
                          placeholder={
                            storedMask(field.key)
                              ? `שמור: ${storedMask(field.key)} · השאירו ריק כדי לא לשנות`
                              : field.placeholder
                          }
                          className={
                            'h-12 w-full rounded-xl border px-3 text-[16px] text-brand-900 outline-none transition-colors ' +
                            (field.readOnly ? 'bg-slate-50 text-slate-600 ' : 'bg-white ') +
                            (err ? 'border-red-400 focus:border-red-400' : 'border-slate-200 focus:border-brand-500')
                          }
                        />
                      ))}

                    {err ? (
                      <p id={`f-${field.key}-err`} className="mt-1 text-[15px] text-red-600">
                        {err}
                      </p>
                    ) : null}
                  </div>
                );
              })}
          </div>

          {confirming && (
            <div ref={confirmRef} className="mt-5 scroll-mb-40">
              <ConfirmIdentity
                next="/"
                reason="כדי לשנות מספר זהות, דרכון או חשבון בנק"
                onConfirmed={() => {
                  setConfirming(false);
                  void advance();
                }}
              />
            </div>
          )}
        </div>

        {/* Sticky action bar */}
        <div className="sticky bottom-0 border-t border-slate-200 bg-[#f6f8f7]/95 px-5 pb-[max(env(safe-area-inset-bottom),1rem)] pt-3 backdrop-blur">
          {/* Save errors live here, right above the button, so they can never sit under the bar. */}
          {serverError && (
            <p className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-[15px] leading-relaxed text-red-700" role="alert">
              {serverError}
            </p>
          )}
          <div className="mb-2 flex h-4 items-center justify-center">
            {saved && (
              <span className="flex items-center gap-1 text-[15px] font-medium text-brand-600">
                <Check className="h-3.5 w-3.5" /> נשמר
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                disabled={saving}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-500 disabled:opacity-50"
                aria-label="חזרה"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={() => advance(false)}
              disabled={saving}
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-brand-500 text-[16px] font-semibold text-white shadow-md transition-colors hover:bg-brand-600 active:scale-[0.99] disabled:opacity-60"
            >
              {saving ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  {isLast ? 'סיום ושמירה' : 'המשך'}
                  {!isLast && <ChevronLeft className="h-5 w-5" />}
                </>
              )}
            </button>
          </div>
          {current.optional && !saving && (
            <button
              onClick={() => advance(true)}
              className="mt-2.5 min-h-[44px] w-full text-center text-[15px] text-slate-600 underline-offset-2 hover:underline"
            >
              {isLast ? 'סיום, אשלים מאוחר יותר' : 'אשלים מאוחר יותר'}
            </button>
          )}
        </div>
      </div>
    </>
  );
}
